// src/utils/werkvormenModal.js - UPDATED: Werkwijze field support + backwards compatibility
import React, { useState } from 'react';

// Helper function to convert werkwijze text to steps array for display
const parseWerkwijzeToSteps = (werkwijze) => {
  if (!werkwijze || typeof werkwijze !== 'string') return [];
  
  // Split on numbered steps, clean up and filter
  const steps = werkwijze
    .split(/\n\s*\n|\n(?=\d+\.)/g) // Split on double newlines or lines starting with numbers
    .map(step => step.trim())
    .filter(step => step.length > 0)
    .map(step => {
      // Clean up step numbering if present
      return step.replace(/^\d+\.\s*/, '').trim();
    })
    .filter(step => step.length > 0);
    
  return steps;
};

// Helper function to check if werkvorm uses new or old format
const getWerkvormenSteps = (werkvorm) => {
  // NEW FORMAT: werkwijze field exists
  if (werkvorm.werkwijze) {
    return parseWerkwijzeToSteps(werkvorm.werkwijze);
  }
  
  // OLD FORMAT: fallback to steps array (backwards compatibility)
  if (werkvorm.steps && Array.isArray(werkvorm.steps)) {
    return werkvorm.steps.filter(step => step && step.trim());
  }
  
  return [];
};

// Modal Component - UPDATED with werkwijze support
export const WerkvormenModal = ({ werkvorm, isOpen, onClose }) => {
  if (!isOpen || !werkvorm) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Helper function to check if array field has content
  const hasContent = (field) => {
    return field && Array.isArray(field) && field.length > 0 && field.some(item => item && item.trim());
  };

  // Helper function to check if string field has content
  const hasStringContent = (field) => {
    return field && typeof field === 'string' && field.trim().length > 0;
  };

  // Get steps from werkwijze or fallback to steps array
  const werkvormenSteps = getWerkvormenSteps(werkvorm);

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={handleBackdropClick}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '15px',
        padding: '2rem',
        maxWidth: '700px',
        maxHeight: '85vh',
        width: '100%',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        position: 'relative',
        overflowY: 'auto'
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '0.5rem',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          ×
        </button>

        {/* Header */}
        <div style={{ marginBottom: '1.5rem', paddingRight: '3rem' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '0.5rem',
            lineHeight: '1.3'
          }}>
            {werkvorm.title}
          </h2>
          
          {/* Meta Info */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '1rem'
          }}>
            </div>
          
          {/* Format Indicator - NEW: Show which format is used */}
          <div style={{
            fontSize: '0.75rem',
            color: '#9ca3af',
            fontStyle: 'italic'
          }}>
            {werkvorm.werkwijze ? '📝 Werkwijze formaat' : '📋 Legacy steps formaat'}
          </div>
        </div>

        {/* Description */}
        {(hasStringContent(werkvorm.description) || hasStringContent(werkvorm.shortDescription)) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{
              color: '#4b5563',
              fontSize: '1rem',
              lineHeight: '1.6',
              margin: 0
            }}>
              {werkvorm.description || werkvorm.shortDescription}
            </p>
          </div>
        )}

        {/* Materials Section */}
        {hasContent(werkvorm.materials) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🛠️ Benodigdheden:
            </h3>
            <ul style={{
              margin: 0,
              paddingLeft: '1.25rem',
              color: '#374151',
              fontSize: '0.875rem',
              lineHeight: '1.5'
            }}>
              {werkvorm.materials.filter(material => material && material.trim()).map((material, index) => (
                <li key={index} style={{ marginBottom: '0.25rem' }}>
                  {material}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* UPDATED: Werkwijze Section - Supports both new and old format */}
        {werkvormenSteps.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📋 Werkwijze:
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {werkvormenSteps.map((step, index) => (
                <div key={index} style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    flexShrink: 0,
                    marginTop: '0.125rem'
                  }}>
                    {index + 1}
                  </div>
                  <p style={{
                    color: '#374151',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FALLBACK: If no werkwijze and no steps, show werkwijze as raw text */}
        {werkvormenSteps.length === 0 && hasStringContent(werkvorm.werkwijze) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📋 Werkwijze:
            </h3>
            
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <pre style={{
                color: '#374151',
                fontSize: '0.875rem',
                lineHeight: '1.6',
                margin: 0,
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit'
              }}>
                {werkvorm.werkwijze}
              </pre>
            </div>
          </div>
        )}

        {/* Tips Section */}
        {hasContent(werkvorm.tips) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              💡 Tips:
            </h3>
            <div style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <ul style={{
                margin: 0,
                paddingLeft: '1.25rem',
                color: '#92400e',
                fontSize: '0.875rem',
                lineHeight: '1.5'
              }}>
                {werkvorm.tips.filter(tip => tip && tip.trim()).map((tip, index) => (
                  <li key={index} style={{ marginBottom: '0.25rem' }}>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Variations Section */}
        {hasContent(werkvorm.variations) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🔄 Variaties:
            </h3>
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #3b82f6',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <ul style={{
                margin: 0,
                paddingLeft: '1.25rem',
                color: '#1e40af',
                fontSize: '0.875rem',
                lineHeight: '1.5'
              }}>
                {werkvorm.variations.filter(variation => variation && variation.trim()).map((variation, index) => (
                  <li key={index} style={{ marginBottom: '0.25rem' }}>
                    {variation}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Debrief Section */}
        {hasStringContent(werkvorm.debrief) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              💬 Debrief Vragen:
            </h3>
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #22c55e',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <p style={{
                color: '#15803d',
                fontSize: '0.875rem',
                lineHeight: '1.5',
                margin: 0
              }}>
                {werkvorm.debrief}
              </p>
            </div>
          </div>
        )}

        {/* Print Hint */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          fontSize: '0.75rem',
          color: '#6b7280',
          textAlign: 'center'
        }}>
          💡 Tip: Gebruik Ctrl+P om deze werkvorm te printen voor gebruik tijdens de sessie
        </div>
      </div>
    </div>
  );
};

// Hook voor modal state management (unchanged)
export const useWerkvormenModal = () => {
  const [selectedWerkvorm, setSelectedWerkvorm] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const openModal = (werkvorm) => {
    setSelectedWerkvorm(werkvorm);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setTimeout(() => setSelectedWerkvorm(null), 200); // Delay for animation
  };

  return {
    selectedWerkvorm,
    isOpen,
    openModal,
    closeModal
  };
};

// Print-specific styles (for when user presses Ctrl+P) - UPDATED
export const addPrintStyles = () => {
  const printStyles = `
    @media print {
      /* Hide everything except modal content */
      body * {
        visibility: hidden;
      }
      
      /* Show only modal content */
      [data-print="werkvorm-modal"] * {
        visibility: visible;
      }
      
      [data-print="werkvorm-modal"] {
        position: static !important;
        max-width: none !important;
        max-height: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      
      /* Hide close button when printing */
      [data-print="werkvorm-modal"] button {
        display: none !important;
      }
      
      /* Optimize spacing for print */
      [data-print="werkvorm-modal"] h2 {
        margin-top: 0 !important;
        page-break-after: avoid;
      }
      
      [data-print="werkvorm-modal"] h3 {
        page-break-after: avoid;
      }
      
      /* Ensure werkwijze steps don't break across pages */
      [data-print="werkvorm-modal"] .werkwijze-step-item {
        page-break-inside: avoid;
        margin-bottom: 0.5rem;
      }
      
      /* Print-friendly colors for sections */
      [data-print="werkvorm-modal"] .tips-section,
      [data-print="werkvorm-modal"] .variations-section,
      [data-print="werkvorm-modal"] .debrief-section,
      [data-print="werkvorm-modal"] .materials-section,
      [data-print="werkvorm-modal"] .werkwijze-section {
        border: 1px solid #000 !important;
        background: white !important;
        color: black !important;
      }
      
      /* Werkwijze text formatting for print */
      [data-print="werkvorm-modal"] .werkwijze-raw-text {
        font-family: 'Times New Roman', serif !important;
        line-height: 1.4 !important;
        font-size: 12pt !important;
      }
    }
  `;

  // Add styles to head if not already present
  if (!document.querySelector('#werkvorm-print-styles')) {
    const style = document.createElement('style');
    style.id = 'werkvorm-print-styles';
    style.textContent = printStyles;
    document.head.appendChild(style);
  }
};

// Enhanced Modal Component with print support - UPDATED
export const WerkvormenModalWithPrint = ({ werkvorm, isOpen, onClose }) => {
  React.useEffect(() => {
    if (isOpen) {
      addPrintStyles();
    }
  }, [isOpen]);

  if (!isOpen || !werkvorm) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Helper functions for content validation
  const hasContent = (field) => {
    return field && Array.isArray(field) && field.length > 0 && field.some(item => item && item.trim());
  };

  const hasStringContent = (field) => {
    return field && typeof field === 'string' && field.trim().length > 0;
  };

  // Get steps using the same logic as main modal
  const werkvormenSteps = getWerkvormenSteps(werkvorm);

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={handleBackdropClick}
    >
      <div 
        data-print="werkvorm-modal"
        style={{
          backgroundColor: 'white',
          borderRadius: '15px',
          padding: '2rem',
          maxWidth: '700px',
          maxHeight: '85vh',
          width: '100%',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          position: 'relative',
          overflowY: 'auto'
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '0.5rem',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          ×
        </button>

        {/* Header */}
        <div style={{ marginBottom: '1.5rem', paddingRight: '3rem' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '0.5rem',
            lineHeight: '1.3'
          }}>
            {werkvorm.title}
          </h2>
          
          {/* Meta Info */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '1rem'
          }}>
            {(werkvorm.duration || werkvorm.estimatedTime) && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                ⏱️ {werkvorm.duration || werkvorm.estimatedTime}
              </span>
            )}
            {werkvorm.participants && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                👥 {werkvorm.participants}
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        {(hasStringContent(werkvorm.description) || hasStringContent(werkvorm.shortDescription)) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{
              color: '#4b5563',
              fontSize: '1rem',
              lineHeight: '1.6',
              margin: 0
            }}>
              {werkvorm.description || werkvorm.shortDescription}
            </p>
          </div>
        )}

        {/* Materials Section */}
        {hasContent(werkvorm.materials) && (
          <div className="materials-section" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🛠️ Benodigdheden:
            </h3>
            <ul style={{
              margin: 0,
              paddingLeft: '1.25rem',
              color: '#374151',
              fontSize: '0.875rem',
              lineHeight: '1.5'
            }}>
              {werkvorm.materials.filter(material => material && material.trim()).map((material, index) => (
                <li key={index} style={{ marginBottom: '0.25rem' }}>
                  {material}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Werkwijze Section - Print optimized */}
        {werkvormenSteps.length > 0 && (
          <div className="werkwijze-section" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📋 Werkwijze:
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {werkvormenSteps.map((step, index) => (
                <div key={index} className="werkwijze-step-item" style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    flexShrink: 0,
                    marginTop: '0.125rem'
                  }}>
                    {index + 1}
                  </div>
                  <p style={{
                    color: '#374151',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Raw Werkwijze fallback */}
        {werkvormenSteps.length === 0 && hasStringContent(werkvorm.werkwijze) && (
          <div className="werkwijze-section" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📋 Werkwijze:
            </h3>
            
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <pre className="werkwijze-raw-text" style={{
                color: '#374151',
                fontSize: '0.875rem',
                lineHeight: '1.6',
                margin: 0,
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit'
              }}>
                {werkvorm.werkwijze}
              </pre>
            </div>
          </div>
        )}

        {/* Tips Section */}
        {hasContent(werkvorm.tips) && (
          <div className="tips-section" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              💡 Tips:
            </h3>
            <div style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <ul style={{
                margin: 0,
                paddingLeft: '1.25rem',
                color: '#92400e',
                fontSize: '0.875rem',
                lineHeight: '1.5'
              }}>
                {werkvorm.tips.filter(tip => tip && tip.trim()).map((tip, index) => (
                  <li key={index} style={{ marginBottom: '0.25rem' }}>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Variations Section */}
        {hasContent(werkvorm.variations) && (
          <div className="variations-section" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🔄 Variaties:
            </h3>
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #3b82f6',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <ul style={{
                margin: 0,
                paddingLeft: '1.25rem',
                color: '#1e40af',
                fontSize: '0.875rem',
                lineHeight: '1.5'
              }}>
                {werkvorm.variations.filter(variation => variation && variation.trim()).map((variation, index) => (
                  <li key={index} style={{ marginBottom: '0.25rem' }}>
                    {variation}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Debrief Section */}
        {hasStringContent(werkvorm.debrief) && (
          <div className="debrief-section" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              💬 Debrief Vragen:
            </h3>
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #22c55e',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <p style={{
                color: '#15803d',
                fontSize: '0.875rem',
                lineHeight: '1.5',
                margin: 0,
                fontStyle: 'italic'
              }}>
                {werkvorm.debrief}
              </p>
            </div>
          </div>
        )}

        {/* Print Hint */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          fontSize: '0.75rem',
          color: '#6b7280',
          textAlign: 'center'
        }}>
          💡 Tip: Gebruik Ctrl+P om deze werkvorm te printen voor gebruik tijdens de sessie
        </div>
      </div>
    </div>
  );
};